import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  IconButton,
  TextField
} from '@mui/material';
import {
  QrCodeScanner as QrCodeScannerIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  VerifiedUser as CertificateIcon,
  QrCodeScanner as QrScanIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { CapacitorNfc } from '@capgo/capacitor-nfc';
import { getApiUrl } from '../utils/apiUrl';
import { DEMO_MODE, demoWorkOrders, demoTeams } from '../services/demoData';

const MobileQRScanner = () => {
  const API_URL = getApiUrl();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [scannedOrder, setScannedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState('');
  const [statusConfigs, setStatusConfigs] = useState([]);
  const [selectedTeamStatus, setSelectedTeamStatus] = useState({});
  const [downloadingModule, setDownloadingModule] = useState(false);
  const [parts, setParts] = useState([]);
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [selectedTeamForCert, setSelectedTeamForCert] = useState(null);
  const [certificateMode, setCertificateMode] = useState(''); // 'scan', 'search', 'manual'
  const [certificateSearchQuery, setCertificateSearchQuery] = useState('');
  const [certificateManualText, setCertificateManualText] = useState('');
  const [scanningCertificate, setScanningCertificate] = useState(false);
  const [nfcSupported, setNfcSupported] = useState(null);
  const [nfcScanningCert, setNfcScanningCert] = useState(false);
  const [confirmStatusDialog, setConfirmStatusDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { teamId, statusConfigId, statusName }
  const [peerReviewStatus, setPeerReviewStatus] = useState({}); // { teamId: { hasPeerReview: bool, peerReviewBy: string } }

  const getToken = () => localStorage.getItem('token');

  // Start skanning automatisk når komponenten lastes eller last forhåndsinnlastet ordre
  useEffect(() => {
    const autoStartScan = async () => {
      // Sjekk om det finnes forhåndsinnlastet ordre
      const preloadQRData = localStorage.getItem('preloadQRData');
      
      if (preloadQRData) {
        // Last forhåndsinnlastet ordre
        console.log('[MobileQRScanner] Loading preloaded order:', preloadQRData);
        localStorage.removeItem('preloadQRData');
        localStorage.removeItem('preloadOrderId');
        await fetchWorkOrderByQR(preloadQRData);
      } else {
        // Normal auto-scan
        await new Promise(resolve => setTimeout(resolve, 300));
        startScanAuto();
      }
    };
    autoStartScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check NFC support on mount
  useEffect(() => {
    const checkNfc = async () => {
      try {
        const { supported } = await CapacitorNfc.isSupported();
        setNfcSupported(supported);
      } catch (e) {
        setNfcSupported(false);
      }
    };
    checkNfc();
  }, []);

  // Start NFC certificate scan
  const startNfcCertificateScan = async () => {
    try {
      setNfcScanningCert(true);
      setError('');

      const { status } = await CapacitorNfc.getStatus();
      if (status === 'NFC_DISABLED') {
        setError('NFC er deaktivert. Aktiver NFC i innstillingene.');
        setNfcScanningCert(false);
        try { await CapacitorNfc.showSettings(); } catch (e) {}
        return;
      }

      let nfcListener = null;
      let timeoutId = null;

      await CapacitorNfc.startScanning({
        invalidateAfterFirstRead: true,
        alertMessage: 'Hold telefonen mot NFC-taggen på materialet',
      });

      nfcListener = await CapacitorNfc.addListener('nfcEvent', async (event) => {
        if (timeoutId) clearTimeout(timeoutId);
        setNfcScanningCert(false);
        try {
          if (nfcListener) await nfcListener.remove();
          await CapacitorNfc.stopScanning();
        } catch (e) {}

        const records = event.tag?.ndefMessage;
        if (records && records.length > 0) {
          const payload = records[0].payload;
          if (payload && payload.length > 0) {
            const langLen = payload[0] & 0x3f;
            const textBytes = payload.slice(1 + langLen);
            const certData = new TextDecoder().decode(new Uint8Array(textBytes));
            console.log('NFC certificate tag read:', certData);
            await saveCertificateToBackend(certData, false, null);
            handleCloseCertificateDialog();
          } else {
            setError('NFC-taggen inneholder ingen data');
          }
        } else {
          setError('Ingen NDEF-data funnet på taggen');
        }
      });

      timeoutId = setTimeout(async () => {
        setNfcScanningCert(false);
        try {
          if (nfcListener) await nfcListener.remove();
          await CapacitorNfc.stopScanning();
        } catch (e) {}
      }, 30000);

    } catch (err) {
      console.error('NFC cert scan error:', err);
      setNfcScanningCert(false);
      if (err.message && err.message.includes('cancel')) return;
      setError(`NFC-feil: ${err.message || 'Ukjent feil'}`);
    }
  };

  // Cancel NFC certificate scan
  const cancelNfcCertScan = async () => {
    try { await CapacitorNfc.stopScanning(); } catch (e) {}
    setNfcScanningCert(false);
  };

  // Sjekk og be om tillatelser
  const requestPermissions = async () => {
    const { camera } = await BarcodeScanner.requestPermissions();
    return camera === 'granted' || camera === 'limited';
  };

  // Hent arbeidsordre basert på QR-data
  const fetchWorkOrderByQR = async (qrData) => {
    setLoading(true);
    setError('');
    
    // Route based on QR prefix
    if (qrData.startsWith('PART:')) {
      try {
        const response = await fetch(
          `${API_URL}/work-orders/parts/search-qr/${encodeURIComponent(qrData)}`,
          { headers: { 'Authorization': `Bearer ${getToken()}` } }
        );
        if (response.ok) {
          const { part, order } = await response.json();
          if (order) {
            setScannedOrder(order);
            setParts([part]);
          } else {
            setError('Del funnet, men ordre ikke funnet');
          }
        } else {
          setError('Del ikke funnet');
        }
        setLoading(false);
        return;
      } catch (err) {
        setError(err.message || 'Kunne ikke søke etter del');
        setLoading(false);
        return;
      }
    }
    
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      console.log('[MobileQRScanner] Demo mode - using demo work order');
      // Find a demo work order or create one based on QR
      const demoOrder = demoWorkOrders[0] || {
        id: 1,
        order_number: qrData,
        customer_name: 'Demo Kunde AS',
        project_name: 'Demo Prosjekt',
        status: 'I gang',
        teams: demoTeams.slice(0, 3).map((t, i) => ({
          id: t.id,
          name: t.team_name,
          status: i === 0 ? 'Ferdig' : i === 1 ? 'I gang' : 'Ikke startet'
        }))
      };
      setScannedOrder(demoOrder);
      setStatusConfigs([
        { id: 1, status_name: 'Ikke startet', color: '#9e9e9e' },
        { id: 2, status_name: 'I gang', color: '#ff9800' },
        { id: 3, status_name: 'Ferdig', color: '#4caf50' },
      ]);
      setParts([
        { id: 1, part_number: 'DEMO-001', description: 'Demo del 1', quantity: 5 },
        { id: 2, part_number: 'DEMO-002', description: 'Demo del 2', quantity: 3 },
      ]);
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/work-orders/search-qr/${encodeURIComponent(qrData)}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Hent brukerens teams
        const myTeams = await fetchMyTeams();
        const myTeamIds = myTeams.map(t => t.id);
        
        // Filtrer arbeidsordre-teams til kun de brukeren er medlem i
        if (data.teams) {
          data.teams = data.teams.filter(team => myTeamIds.includes(team.id));
        }
        
        setScannedOrder(data);
        
        // Hent status-konfigurasjoner
        await fetchStatusConfigs();
        
        // Hent team-status for denne ordren
        await fetchTeamStatuses(data.id);
        
        // Hent deler for denne ordren
        await fetchParts(data.id);
        
        // Hent peer review status for sidemannskontroll
        await fetchPeerReviewStatus(data.id);
      } else {
        const errorData = await response.json();
        console.error('[MobileQRScanner] API error:', errorData);
        const errorMsg = errorData.message || 'Kunne ikke finne arbeidsordre';
        const debugInfo = errorData.receivedData ? `\nMottatt: ${errorData.receivedData}\nForventet: ${errorData.expectedFormat}` : '';
        setError(errorMsg + debugInfo);
      }
    } catch (err) {
      console.error('[MobileQRScanner] Error fetching work order:', err);
      setError('Kunne ikke hente arbeidsordre: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Hent brukerens teams
  const fetchMyTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/teams/my-teams`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Error fetching my teams:', err);
    }
    return [];
  };

  // Hent tilgjengelige status-konfigurasjoner
  const fetchStatusConfigs = async () => {
    try {
      const response = await fetch(`${API_URL}/status-configs`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const configs = await response.json();
        setStatusConfigs(configs);
      }
    } catch (err) {
      console.error('Error fetching status configs:', err);
    }
  };

  // Hent team-status for arbeidsordren
  const fetchTeamStatuses = async (workOrderId) => {
    try {
      const response = await fetch(
        `${API_URL}/team-status-reports/work-order/${workOrderId}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      if (response.ok) {
        const reports = await response.json();
        // Lag en mapping av teamId til status (ignorer peer review rapporter for status-visning)
        const statusMap = {};
        // Filtrer ut peer review rapporter for å finne faktisk status
        const nonPeerReviewReports = reports.filter(r => !r.isPeerReview);
        nonPeerReviewReports.forEach(report => {
          if (!statusMap[report.teamId] || new Date(report.createdAt) > new Date(statusMap[report.teamId].createdAt)) {
            statusMap[report.teamId] = report;
          }
        });
        setSelectedTeamStatus(statusMap);
      }
    } catch (err) {
      console.error('Error fetching team statuses:', err);
    }
  };

  // Hent deler for arbeidsordren
  const fetchParts = async (workOrderId) => {
    try {
      const response = await fetch(
        `${API_URL}/work-orders/${workOrderId}/parts`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      if (response.ok) {
        const partsData = await response.json();
        setParts(partsData);
      }
    } catch (err) {
      console.error('Error fetching parts:', err);
    }
  };

  // Hent peer review status for sidemannskontroll
  const fetchPeerReviewStatus = async (workOrderId) => {
    try {
      const response = await fetch(
        `${API_URL}/team-status-reports/work-order/${workOrderId}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      if (response.ok) {
        const reports = await response.json();
        // Finn peer review reports for hvert team
        const peerStatus = {};
        reports.forEach(report => {
          if (report.isPeerReview || report.is_peer_review) {
            const name = report.reporterName || report.reporter_name;
            peerStatus[report.teamId || report.team_id] = {
              hasPeerReview: true,
              peerReviewBy: (name && name.trim()) ? name.trim() : 'Ukjent'
            };
          }
        });
        setPeerReviewStatus(peerStatus);
      }
    } catch (err) {
      console.error('Error fetching peer review status:', err);
    }
  };

  // Håndter status-valg (med bekreftelse)
  const handleStatusChange = (teamId, statusConfigId) => {
    if (!scannedOrder) return;

    // Sjekk om valgt status er "Sertifikater"
    const selectedConfig = statusConfigs.find(config => config.id === statusConfigId);
    if (selectedConfig && selectedConfig.name.toLowerCase().includes('sertifikat')) {
      // Åpne sertifikat-dialog i stedet for å oppdatere status direkte
      const team = scannedOrder.teams.find(t => t.id === teamId);
      handleOpenCertificateDialog(team);
      return;
    }

    // Sjekk om dette er sidemannskontroll
    const isPeerReview = statusConfigId === 'SIDEMANNSKONTROLL';
    const team = scannedOrder.teams.find(t => t.id === teamId);

    // Vis bekreftelsesdialog
    setPendingStatusChange({
      teamId,
      statusConfigId: isPeerReview ? null : statusConfigId,
      statusName: isPeerReview ? 'Sidemannskontroll' : (selectedConfig?.name || 'Ukjent'),
      teamName: team?.name || 'Ukjent team',
      isPeerReview
    });
    setConfirmStatusDialog(true);
  };

  // Bekreft status-endring
  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    
    setConfirmStatusDialog(false);
    
    if (pendingStatusChange.isPeerReview) {
      await registerPeerReview(pendingStatusChange.teamId);
    } else {
      await updateTeamStatus(pendingStatusChange.teamId, pendingStatusChange.statusConfigId);
    }
    setPendingStatusChange(null);
  };

  // Registrer sidemannskontroll
  const registerPeerReview = async (teamId) => {
    if (!scannedOrder) return;

    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      // Finn "Ferdig" status config
      const ferdigConfig = statusConfigs.find(c => 
        c.name.toLowerCase() === 'ferdig' || c.name.toLowerCase() === 'fullført'
      );
      
      if (!ferdigConfig) {
        setError('Kunne ikke finne "Ferdig" status i systemet');
        return;
      }

      const response = await fetch(
        `${API_URL}/team-status-reports`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            workOrderId: scannedOrder.id,
            teamId: teamId,
            statusConfigId: ferdigConfig.id,
            isPeerReview: true
          })
        }
      );

      if (response.ok) {
        setSuccess('👥 Sidemannskontroll registrert!');
        // Refresh statuses
        await fetchTeamStatuses(scannedOrder.id);
        await fetchPeerReviewStatus(scannedOrder.id);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Kunne ikke registrere sidemannskontroll');
      }
    } catch (err) {
      console.error('Error registering peer review:', err);
      setError('Kunne ikke registrere sidemannskontroll');
    } finally {
      setUpdating(false);
    }
  };

  // Avbryt status-endring
  const cancelStatusChange = () => {
    setConfirmStatusDialog(false);
    setPendingStatusChange(null);
    // Nullstill dropdown til forrige verdi
    if (scannedOrder) {
      fetchTeamStatuses(scannedOrder.id);
    }
  };

  // Oppdater team-status (etter bekreftelse)
  const updateTeamStatus = async (teamId, statusConfigId) => {
    if (!scannedOrder) return;

    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `${API_URL}/team-status-reports`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            workOrderId: scannedOrder.id,
            teamId: teamId,
            statusConfigId: statusConfigId
          })
        }
      );

      if (response.ok) {
        setSuccess('Team-status oppdatert!');
        // Refresh team statuses
        await fetchTeamStatuses(scannedOrder.id);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Kunne ikke oppdatere team-status');
      }
    } catch (err) {
      console.error('Error updating team status:', err);
      setError('Kunne ikke oppdatere team-status');
    } finally {
      setUpdating(false);
    }
  };

  // Åpne sertifikat-dialog
  const handleOpenCertificateDialog = (team) => {
    setSelectedTeamForCert(team);
    setCertificateDialogOpen(true);
    setCertificateMode('');
  };

  // Lukk sertifikat-dialog
  const handleCloseCertificateDialog = () => {
    setCertificateDialogOpen(false);
    setSelectedTeamForCert(null);
    setCertificateMode('');
    setCertificateSearchQuery('');
    setCertificateManualText('');
    setScanningCertificate(false);
  };

  // Start skanning av sertifikat QR-kode
  const startCertificateScan = async () => {
    try {
      setScanningCertificate(true);
      setError('');

      const granted = await requestPermissions();
      if (!granted) {
        setError('Kamera-tillatelse er nødvendig for å skanne QR-koder');
        setScanningCertificate(false);
        return;
      }

      const { barcodes } = await BarcodeScanner.scan();
      setScanningCertificate(false);

      if (barcodes && barcodes.length > 0) {
        const certData = barcodes[0].rawValue;
        console.log('Certificate QR scanned:', certData);
        
        // Lagre skannet sertifikat til backend
        await saveCertificateToBackend(certData, false, null);
        handleCloseCertificateDialog();
      } else {
        setError('Ingen QR-kode funnet');
      }
    } catch (err) {
      console.error('Certificate scan error:', err);
      setScanningCertificate(false);
      setError('Kunne ikke skanne sertifikat QR-kode');
    }
  };

  // Søk i sertifikat-database
  const handleCertificateSearch = async () => {
    if (!certificateSearchQuery.trim()) {
      setError('Vennligst skriv inn søkeord');
      return;
    }
    
    try {
      setLoading(true);
      
      // Søk i steel_certificates databasen
      const response = await fetch(
        `${API_URL}/steel-certificates/search?query=${encodeURIComponent(certificateSearchQuery)}`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      
      if (response.ok) {
        const results = await response.json();
        
        if (results.length > 0) {
          // Bruk første resultat
          const certificate = results[0];
          await saveCertificateToBackend(
            certificate.certificate_number,
            false,
            certificate.id
          );
          handleCloseCertificateDialog();
        } else {
          setError('Ingen sertifikater funnet. Prøv et annet søkeord.');
        }
      } else {
        setError('Kunne ikke søke i sertifikat-database');
      }
    } catch (err) {
      console.error('Certificate search error:', err);
      setError('Kunne ikke søke i sertifikat-database');
    } finally {
      setLoading(false);
    }
  };

  // Lagre manuelt sertifikat
  const handleManualCertificate = async () => {
    if (!certificateManualText.trim()) {
      setError('Vennligst skriv inn sertifikat-informasjon');
      return;
    }
    
    try {
      setLoading(true);
      // Lagre manuelt sertifikat til backend
      await saveCertificateToBackend(certificateManualText, true, null);
      handleCloseCertificateDialog();
    } catch (err) {
      console.error('Manual certificate error:', err);
      setError('Kunne ikke lagre sertifikat');
    } finally {
      setLoading(false);
    }
  };

  // Lagre sertifikat til backend
  const saveCertificateToBackend = async (certificateNumber, isManual, certificateId) => {
    if (!scannedOrder || !selectedTeamForCert) {
      setError('Mangler ordre eller team informasjon');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/work-orders/${scannedOrder.id}/certificates`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            certificates: [
              {
                certificateNumber: certificateNumber,
                isManual: isManual,
                certificateId: certificateId
              }
            ],
            teamId: selectedTeamForCert.id
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(`✅ Sertifikat registrert: ${certificateNumber}`);
        console.log('Certificate saved:', data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Kunne ikke lagre sertifikat');
      }
    } catch (err) {
      console.error('Error saving certificate:', err);
      setError('Kunne ikke lagre sertifikat');
    }
  };

  // Start skanning automatisk (uten å resette state)
  const startScanAuto = async () => {
    try {
      setError('');

      if (!BarcodeScanner) {
        setError('Barcode scanner er ikke tilgjengelig på denne enheten');
        return;
      }

      const granted = await requestPermissions();
      if (!granted) {
        setError('Kamera-tillatelse er nødvendig for å skanne QR-koder.');
        return;
      }

      setScanning(true);

      const { barcodes } = await BarcodeScanner.scan();
      setScanning(false);

      if (barcodes && barcodes.length > 0) {
        const qrData = barcodes[0].rawValue;
        console.log('QR Code scanned:', qrData);
        await fetchWorkOrderByQR(qrData);
      } else {
        setError('Ingen QR-kode funnet');
      }
    } catch (err) {
      console.error('Scan error:', err);
      setScanning(false);
      
      if (err.message && err.message.includes('cancelled')) {
        return;
      }
      
      setError(`Kunne ikke skanne: ${err.message || 'Ukjent feil'}`);
    }
  };

  // Start skanning
  const startScan = async () => {
    try {
      setError('');
      setScannedOrder(null);

      // Sjekk om BarcodeScanner er tilgjengelig
      if (!BarcodeScanner) {
        setError('Barcode scanner er ikke tilgjengelig på denne enheten');
        console.error('BarcodeScanner plugin not available');
        return;
      }

      // Sjekk om Google Play Services ML Kit modul er tilgjengelig
      console.log('Checking if ML Kit module is available...');
      try {
        const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
        console.log('ML Kit module available:', available);
        
        if (!available) {
          console.log('ML Kit module not available, attempting to install...');
          setDownloadingModule(true);
          
          try {
            await BarcodeScanner.installGoogleBarcodeScannerModule();
            console.log('ML Kit module installation initiated');
            
            // Vent litt for at installasjonen skal starte
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Sjekk igjen om modulen nå er tilgjengelig
            const { available: nowAvailable } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
            
            if (!nowAvailable) {
              setDownloadingModule(false);
              setError('Google Play Services ML Kit må installeres manuelt. Gå til Google Play Store, søk etter "Google Play Services" og oppdater. Eller vent 1-2 minutter hvis nedlasting pågår i bakgrunnen.');
              return;
            }
            
            setDownloadingModule(false);
            console.log('ML Kit module now available');
          } catch (installErr) {
            console.error('ML Kit installation error:', installErr);
            setDownloadingModule(false);
            setError('Kunne ikke installere skanner-modul. Prøv å oppdatere Google Play Services manuelt fra Google Play Store. Feil: ' + (installErr.message || 'Ukjent'));
            return;
          }
        } else {
          console.log('ML Kit module already available');
        }
      } catch (checkErr) {
        console.error('ML Kit check error:', checkErr);
        // Fortsett likevel, kanskje det fungerer
        console.log('Continuing despite check error...');
      }

      // Be om tillatelser
      console.log('Requesting camera permissions...');
      const granted = await requestPermissions();
      console.log('Permission granted:', granted);
      
      if (!granted) {
        setError('Kamera-tillatelse er nødvendig for å skanne QR-koder. Gå til Innstillinger > Apper > Produksjonsstyring > Tillatelser og aktiver Kamera.');
        return;
      }

      setScanning(true);

      // Start skanning
      console.log('Starting camera scan...');
      const { barcodes } = await BarcodeScanner.scan();
      console.log('Scan completed, barcodes found:', barcodes?.length || 0);

      setScanning(false);

      if (barcodes && barcodes.length > 0) {
        const qrData = barcodes[0].rawValue;
        console.log('QR Code scanned:', qrData);
        await fetchWorkOrderByQR(qrData);
      } else {
        setError('Ingen QR-kode funnet');
      }
    } catch (err) {
      console.error('Scan error:', err);
      console.error('Error details:', JSON.stringify(err));
      setScanning(false);
      
      // Mer spesifikk feilmelding
      if (err.message && err.message.includes('permission')) {
        setError('Kamera-tilgang nektet. Vennligst aktiver kamera-tillatelse i enhetens innstillinger.');
      } else if (err.message && err.message.includes('camera')) {
        setError('Kunne ikke starte kameraet. Sjekk at ingen andre apper bruker kameraet.');
      } else {
        setError(`Kunne ikke skanne QR-kode: ${err.message || 'Ukjent feil'}`);
      }
    }
  };

  // Status farge
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ny':
        return 'primary';
      case 'pågår':
        return 'warning';
      case 'fullført':
        return 'success';
      case 'avbrutt':
        return 'error';
      default:
        return 'default';
    }
  };

  // Status label
  const getStatusLabel = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Ukjent';
  };

  return (
    <Box>
      {/* Skanning aktiv */}
      {scanning && (
        <Card elevation={3} sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <QrCodeScannerIcon sx={{ fontSize: 80, color: 'white', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
              Skanner...
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}>
              Hold kamera mot QR-koden på arbeidsordren
            </Typography>
            <CircularProgress sx={{ color: 'white' }} />
          </CardContent>
        </Card>
      )}

      {/* Modul nedlasting melding */}
      {downloadingModule && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">Laster ned skanner-modul...</Typography>
          <Typography variant="caption">
            Google Play Services laster ned ML Kit barcode scanner. Dette tar 10-30 sekunder første gang. 
            Sørg for at du har internett-tilkobling.
          </Typography>
        </Alert>
      )}

      {/* Error melding */}
      {error && !downloadingModule && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Skannet arbeidsordre */}
      {scannedOrder && (
        <Card elevation={2}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" fontSize="large" />
              <Typography variant="h6" fontWeight="bold" color="success.main">
                Arbeidsordre funnet!
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Ordrenummer og status */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5" fontWeight="bold">
                #{scannedOrder.orderNumber}
              </Typography>
              <Chip
                label={getStatusLabel(scannedOrder.status)}
                color={getStatusColor(scannedOrder.status)}
                size="medium"
              />
            </Box>

            {/* Kunde */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BusinessIcon color="action" />
                <Typography variant="body1" fontWeight="500">
                  {scannedOrder.customer || 'Ingen kunde'}
                </Typography>
              </Box>
              {scannedOrder.deliveryDate && (() => {
                const deliveryDate = new Date(scannedOrder.deliveryDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isOverdue = deliveryDate < today && scannedOrder.status !== 'ferdig' && scannedOrder.status !== 'avbrutt';
                
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4 }}>
                    <CalendarIcon sx={{ 
                      fontSize: 18, 
                      color: isOverdue ? 'error.main' : 'text.secondary' 
                    }} />
                    <Typography 
                      variant="body2" 
                      color={isOverdue ? 'error.main' : 'text.secondary'}
                      fontWeight={isOverdue ? 600 : 400}
                    >
                      Levering: {deliveryDate.toLocaleDateString('nb-NO', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      })}
                      {isOverdue && ' ⚠️ FORSINKET'}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>

            {/* Materialer */}
            {scannedOrder.materials && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Materialer:
                </Typography>
                <Typography variant="body2">
                  {scannedOrder.materials}
                </Typography>
              </Box>
            )}

            {/* Beskrivelse */}
            {scannedOrder.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Beskrivelse:
                </Typography>
                <Typography variant="body2">
                  {scannedOrder.description}
                </Typography>
              </Box>
            )}

            {/* Memo */}
            {scannedOrder.memo && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Memo:
                  </Typography>
                  <Typography variant="body2">
                    {scannedOrder.memo}
                  </Typography>
                </Box>
              </>
            )}

            {/* Deler */}
            {parts && parts.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BuildIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Deler ({parts.length})
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell><strong>Navn</strong></TableCell>
                        <TableCell align="center"><strong>Antall</strong></TableCell>
                        <TableCell><strong>Material</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parts.map((part) => (
                        <TableRow key={part.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {part.navn || '-'}
                            </Typography>
                            {(part.tykkelse || part.ordrenummer) && (
                              <Typography variant="caption" color="text.secondary">
                                {part.tykkelse && `${part.tykkelse}mm`}
                                {part.tykkelse && part.ordrenummer && ' • '}
                                {part.ordrenummer && `#${part.ordrenummer}`}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {part.antall || 0}
                            </Typography>
                            {part.antallProdusert > 0 && (
                              <Typography variant="caption" color="success.main">
                                {part.antallProdusert} prod.
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {part.material || '-'}
                            </Typography>
                            {(part.gjenging || part.maskinering || part.knekking || part.sveising) && (
                              <Box sx={{ mt: 0.5 }}>
                                {part.gjenging && <Chip label="Gjenging" size="small" sx={{ mr: 0.5, mb: 0.5 }} />}
                                {part.maskinering && <Chip label="Maskinering" size="small" sx={{ mr: 0.5, mb: 0.5 }} />}
                                {part.knekking && <Chip label="Knekking" size="small" sx={{ mr: 0.5, mb: 0.5 }} />}
                                {part.sveising && <Chip label="Sveising" size="small" sx={{ mr: 0.5, mb: 0.5 }} />}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Team-status oppdatering */}
            <Divider sx={{ my: 3 }} />
            
            {/* Success melding */}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            {scannedOrder.teams && scannedOrder.teams.length > 0 ? (
              <>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Oppdater Team-Status
                </Typography>
                {scannedOrder.teams.map((team) => {
                  const hasPeerReview = peerReviewStatus[team.id]?.hasPeerReview;
                  // eslint-disable-next-line no-unused-vars
                  const currentStatus = selectedTeamStatus[team.id]?.statusName?.toLowerCase();
                  const isPeerReviewStatus = selectedTeamStatus[team.id]?.isPeerReview;
                  const hasSidemannskontroll = team.sidemannskontroll === true || team.sidemannskontroll === 1;
                  
                  return (
                  <Box key={team.id} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {team.name} {team.sequence && `(Sekvens ${team.sequence})`}
                      </Typography>
                      {hasSidemannskontroll && (
                        <Chip
                          icon={<GroupIcon sx={{ fontSize: 14 }} />}
                          label="Sidemannskontroll"
                          size="small"
                          sx={{
                            bgcolor: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fca5a5',
                            fontSize: '0.65rem',
                            height: 22
                          }}
                        />
                      )}
                    </Box>
                    
                    {/* Vis peer review status hvis utført */}
                    {(hasPeerReview || team.isPeerReview) && (
                      <Alert severity="success" sx={{ mb: 1, py: 0 }}>
                        <Typography variant="caption">
                          👥 Sidemannskontroll utført av {team.statusReportedByName || team.status_reported_by_name || peerReviewStatus[team.id]?.peerReviewBy || 'Ukjent'}
                        </Typography>
                      </Alert>
                    )}
                    
                    {/* Vis hvis venter på sidemannskontroll */}
                    {hasSidemannskontroll && isPeerReviewStatus && !hasPeerReview && (
                      <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                        <Typography variant="caption">
                          ⏳ Venter på sidemannskontroll
                        </Typography>
                      </Alert>
                    )}
                    
                    {selectedTeamStatus[team.id] && (
                      <Chip 
                        label={selectedTeamStatus[team.id].statusName || 'Ingen status'}
                        size="small"
                        sx={{ 
                          mb: 1,
                          bgcolor: selectedTeamStatus[team.id].statusColor || '#gray',
                          color: 'white'
                        }}
                      />
                    )}
                    <FormControl fullWidth size="small">
                      <InputLabel>Velg status</InputLabel>
                      <Select
                        value={selectedTeamStatus[team.id]?.statusConfigId || ''}
                        onChange={(e) => handleStatusChange(team.id, e.target.value)}
                        label="Velg status"
                        disabled={updating}
                      >
                        {/* Sidemannskontroll-valg for teams som krever det */}
                        {hasSidemannskontroll && !hasPeerReview && (
                          <MenuItem value="SIDEMANNSKONTROLL" sx={{ color: '#dc2626', fontWeight: 600 }}>
                            👥 Sidemannskontroll
                          </MenuItem>
                        )}
                        {statusConfigs.map((config) => (
                          <MenuItem key={config.id} value={config.id}>
                            {config.icon} {config.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  );
                })}
              </>
            ) : (
              <Alert severity="info">
                Du er ikke medlem i noen av teams for denne arbeidsordren.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Skann på nytt knapp */}
            <Button
              fullWidth
              variant="outlined"
              onClick={startScan}
              startIcon={<QrCodeScannerIcon />}
            >
              Skann ny QR-kode
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ML Kit hjelp hvis feil */}
      {error && error.includes('Google Play Services') && (
        <Card elevation={1} sx={{ mt: 3, bgcolor: '#fff3cd', border: '1px solid #ffc107' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="error">
              🔧 Fikse skanner-problem:
            </Typography>
            <List dense>
              <ListItem>
                <Typography variant="body2">
                  <strong>1.</strong> Åpne <strong>Google Play Store</strong>
                </Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body2">
                  <strong>2.</strong> Søk etter <strong>"Google Play Services"</strong>
                </Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body2">
                  <strong>3.</strong> Klikk <strong>"Oppdater"</strong> hvis tilgjengelig
                </Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body2">
                  <strong>4.</strong> Vent til oppdatering er ferdig (1-2 min)
                </Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body2">
                  <strong>5.</strong> Kom tilbake og prøv "Start Skanning" igjen
                </Typography>
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              Hvis problemet vedvarer, restart telefonen og prøv igjen.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Skann på nytt hvis feil eller avbrutt */}
      {!scannedOrder && !loading && !scanning && (
        <Card elevation={3} sx={{ mt: 3, textAlign: 'center' }}>
          <CardContent sx={{ py: 4 }}>
            <QrCodeScannerIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {error ? 'Skanning feilet' : 'Klar til å skanne'}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={startScan}
              startIcon={<QrCodeScannerIcon />}
              sx={{ mt: 2 }}
            >
              {error ? 'Prøv igjen' : 'Start Skanning'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sertifikat Dialog */}
      <Dialog 
        open={certificateDialogOpen} 
        onClose={handleCloseCertificateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CertificateIcon color="primary" />
              <Typography variant="h6">Sertifikat</Typography>
            </Box>
            <IconButton onClick={handleCloseCertificateDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedTeamForCert && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedTeamForCert.name}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent>
          {/* Velg metode */}
          {!certificateMode && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Velg hvordan du vil legge til sertifikat:
              </Typography>
              
              <ButtonGroup orientation="vertical" fullWidth sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<QrScanIcon />}
                  onClick={() => setCertificateMode('scan')}
                  sx={{ py: 2, justifyContent: 'flex-start' }}
                >
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold">
                      QR-kode Skanning
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Skann sertifikat QR-kode med kamera
                    </Typography>
                  </Box>
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => setCertificateMode('search')}
                  sx={{ py: 2, justifyContent: 'flex-start' }}
                >
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Søk i Database
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Søk etter eksisterende sertifikat
                    </Typography>
                  </Box>
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setCertificateMode('manual')}
                  sx={{ py: 2, justifyContent: 'flex-start' }}
                >
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold">
                      Manuell Innskriving
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Skriv inn sertifikat-informasjon manuelt
                    </Typography>
                  </Box>
                </Button>

                {nfcSupported && (
                  <Button
                    variant="outlined"
                    onClick={() => setCertificateMode('nfc')}
                    sx={{ py: 2, justifyContent: 'flex-start', borderColor: '#8b5cf6', color: '#8b5cf6' }}
                  >
                    <Box sx={{ textAlign: 'left', flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontSize: '1.3rem' }}>📡</Typography>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          NFC-tag Skanning
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Les sertifikat fra NFC-tag på materialet
                        </Typography>
                      </Box>
                    </Box>
                  </Button>
                )}
              </ButtonGroup>
            </Box>
          )}

          {/* QR-kode Skanning Modus */}
          {certificateMode === 'scan' && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <QrScanIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                Skann sertifikat QR-kode
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Trykk på knappen nedenfor for å starte kamera
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={startCertificateScan}
                disabled={scanningCertificate}
                startIcon={scanningCertificate ? <CircularProgress size={20} /> : <QrScanIcon />}
                sx={{ mt: 3 }}
              >
                {scanningCertificate ? 'Skanner...' : 'Start Skanning'}
              </Button>
            </Box>
          )}

          {/* Søk Modus */}
          {certificateMode === 'search' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Søk i sertifikat-database:
              </Typography>
              <TextField
                fullWidth
                label="Søk etter sertifikat"
                placeholder="F.eks. sertifikatnummer, materiale..."
                value={certificateSearchQuery}
                onChange={(e) => setCertificateSearchQuery(e.target.value)}
                variant="outlined"
                margin="normal"
                autoFocus
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleCertificateSearch}
                disabled={loading || !certificateSearchQuery.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ mt: 2 }}
              >
                {loading ? 'Søker...' : 'Søk'}
              </Button>
            </Box>
          )}

          {/* Manuell Modus */}
          {certificateMode === 'manual' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Skriv inn sertifikat-informasjon:
              </Typography>
              <TextField
                fullWidth
                label="Sertifikat-informasjon"
                placeholder="F.eks. sertifikatnummer, leverandør, dato..."
                value={certificateManualText}
                onChange={(e) => setCertificateManualText(e.target.value)}
                variant="outlined"
                margin="normal"
                multiline
                rows={4}
                autoFocus
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleManualCertificate}
                disabled={loading || !certificateManualText.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
                sx={{ mt: 2 }}
              >
                {loading ? 'Lagrer...' : 'Lagre Sertifikat'}
              </Button>
            </Box>
          )}

          {/* NFC Modus */}
          {certificateMode === 'nfc' && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              {nfcScanningCert ? (
                <>
                  <Typography sx={{ fontSize: 80, mb: 2 }}>📡</Typography>
                  <Typography variant="h6" gutterBottom sx={{ color: '#8b5cf6' }}>
                    Skanner NFC...
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Hold telefonen mot NFC-taggen på materialet
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={cancelNfcCertScan}
                    sx={{ borderColor: '#8b5cf6', color: '#8b5cf6' }}
                  >
                    Avbryt
                  </Button>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: 80, mb: 2 }}>📡</Typography>
                  <Typography variant="body1" gutterBottom>
                    Les sertifikat fra NFC-tag
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Trykk på knappen og hold telefonen mot NFC-taggen
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={startNfcCertificateScan}
                    sx={{ mt: 3, py: 1.5, bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
                  >
                    📡 Start NFC Skanning
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {certificateMode && (
            <Button onClick={() => setCertificateMode('')} color="inherit">
              Tilbake
            </Button>
          )}
          <Button onClick={handleCloseCertificateDialog} color="inherit">
            Avbryt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bekreftelsesdialog for status-endring */}
      <Dialog
        open={confirmStatusDialog}
        onClose={cancelStatusChange}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Bekreft statusendring
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Er du sikker på at du vil endre status til:
            </Typography>
            
            {pendingStatusChange && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Team:
                </Typography>
                <Typography variant="body1" fontWeight="bold" gutterBottom>
                  {pendingStatusChange.teamName}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  {pendingStatusChange.isPeerReview ? 'Aksjon:' : 'Ny status:'}
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight="bold"
                  sx={{ 
                    color: pendingStatusChange.isPeerReview ? '#dc2626' : 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  {pendingStatusChange.isPeerReview && <GroupIcon />}
                  {pendingStatusChange.statusName}
                </Typography>
              </Box>
            )}
            
            <Alert severity={pendingStatusChange?.isPeerReview ? 'warning' : 'info'} sx={{ mt: 2 }}>
              {pendingStatusChange?.isPeerReview 
                ? 'Du bekrefter at du har utført sidemannskontroll på dette teamets arbeid.'
                : 'Denne endringen vil bli registrert i systemet.'
              }
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={cancelStatusChange} color="inherit">
            Avbryt
          </Button>
          <Button 
            onClick={confirmStatusChange} 
            variant="contained" 
            color="primary"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={20} /> : null}
          >
            {updating ? 'Oppdaterer...' : 'Bekreft'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileQRScanner;
